const GOOGLE_API_SCRIPT_URL = "https://apis.google.com/js/api.js";

let gapiLoaded = false;
let pickerLoaded = false;

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
    });
}

async function ensureGapiLoaded(): Promise<void> {
    if (gapiLoaded && pickerLoaded) return;

    if (!gapiLoaded) {
        await loadScript(GOOGLE_API_SCRIPT_URL);
        gapiLoaded = true;
    }

    if (!pickerLoaded) {
        await new Promise<void>((resolve) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).gapi.load("picker", { callback: resolve });
        });
        pickerLoaded = true;
    }
}

export interface GoogleDriveFile {
    id: string;
    name: string;
    mimeType: string;
    url: string;
}

export interface OpenPickerOptions {
    accessToken: string;
    developerKey?: string;
}

export async function openGoogleDrivePicker(options: OpenPickerOptions): Promise<GoogleDriveFile[]> {
    await ensureGapiLoaded();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const google = (window as any).google;

    return new Promise((resolve) => {
        // Radix Dialog sets body.style.pointerEvents = "none" for modal dialogs.
        // The Picker is injected as a child of <body> and inherits this, making it
        // unclickable. Restore pointer events while the picker is open.
        const savedPointerEvents = document.body.style.pointerEvents;
        document.body.style.pointerEvents = "";

        const docsView = new google.picker.DocsView().setIncludeFolders(true).setSelectFolderEnabled(false);

        const builder = new google.picker.PickerBuilder()
            .setOAuthToken(options.accessToken)
            .addView(docsView)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .setCallback((data: any) => {
                if (data.action === google.picker.Action.PICKED) {
                    document.body.style.pointerEvents = savedPointerEvents;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const files: GoogleDriveFile[] = data.docs.map((doc: any) => ({
                        id: doc.id,
                        name: doc.name,
                        mimeType: doc.mimeType,
                        url: doc.url,
                    }));
                    resolve(files);
                } else if (data.action === google.picker.Action.CANCEL) {
                    document.body.style.pointerEvents = savedPointerEvents;
                    resolve([]);
                }
            })
            .enableFeature(google.picker.Feature.MULTISELECT_ENABLED);

        if (options.developerKey) {
            builder.setDeveloperKey(options.developerKey);
        }

        const picker = builder.build();
        picker.setVisible(true);
    });
}
